import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  NotificationsService,
  type NotificationKind,
  type NotificationPreferences,
  type ScheduleScope,
} from '../client'
import Spinner from '../components/chat/Spinner'
import Toggle from '../components/ui/Toggle'
import { apiErrorMessage } from '../lib/api'

const SCOPES: { value: ScheduleScope; label: string; detail: string }[] = [
  { value: 'mine', label: 'Schedules I set up', detail: 'Only runs of workflows you scheduled.' },
  { value: 'all', label: 'Every schedule on the brand', detail: 'Runs scheduled by anyone on the team.' },
  { value: 'none', label: 'Nothing', detail: 'Check the run log on Workflows instead.' },
]

const KINDS: { value: NotificationKind; label: string }[] = [
  { value: 'run_needs_approval', label: 'A run is waiting for an approval' },
  { value: 'run_failed', label: 'A run failed or stopped at a step' },
  { value: 'run_skipped', label: 'A run was skipped' },
  { value: 'run_completed', label: 'A run finished' },
]

export default function Notifications() {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)

  useEffect(() => {
    NotificationsService.getPreferences()
      .then(setPrefs)
      .catch((err: unknown) => toast.error(apiErrorMessage(err)))
  }, [])

  async function update(change: Partial<NotificationPreferences>) {
    if (!prefs) return
    const previous = prefs
    const next = { ...prefs, ...change }
    setPrefs(next)
    try {
      setPrefs(await NotificationsService.setPreferences(next))
    } catch (err) {
      setPrefs(previous)
      toast.error(apiErrorMessage(err))
    }
  }

  const kinds = prefs?.kinds ?? []

  return (
    <div className="flex flex-1 flex-col">
      <header className="border-b border-stone-200 px-4 py-4 sm:px-6">
        <h1 className="text-base font-medium text-stone-900">Notifications</h1>
        <p className="mt-0.5 text-sm text-stone-500">
          What the bell tells you about scheduled workflow runs. These settings apply to every brand you're on.
        </p>
      </header>

      {prefs === null ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-5 border-purple-200 border-t-purple-700" />
        </div>
      ) : (
        <div className="max-w-xl space-y-8 px-4 py-6 sm:px-6">
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium text-stone-900">Tell me about</legend>
            {SCOPES.map((scope) => (
              <label
                key={scope.value}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 px-3 py-2.5 has-checked:border-purple-300 has-checked:bg-purple-50/50"
              >
                <input
                  type="radio"
                  name="schedule-scope"
                  checked={prefs.schedule_scope === scope.value}
                  onChange={() => void update({ schedule_scope: scope.value })}
                  className="mt-0.5 size-4 border-stone-300 text-purple-700 focus:ring-purple-200"
                />
                <span>
                  <span className="block text-sm text-stone-900">{scope.label}</span>
                  <span className="block text-xs text-stone-500">{scope.detail}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <section className={prefs.schedule_scope === 'none' ? 'opacity-50' : ''}>
            <p className="mb-2 text-sm font-medium text-stone-900">When</p>
            <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200">
              {KINDS.map((kind) => (
                <li key={kind.value} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <span className="text-sm text-stone-800">{kind.label}</span>
                  <Toggle
                    checked={kinds.includes(kind.value)}
                    onChange={(on) =>
                      void update({
                        kinds: on ? [...kinds, kind.value] : kinds.filter((value) => value !== kind.value),
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  )
}
